import { useCallback } from 'react';
import { useNotifications } from '../contexts/NotificationContext';

export const useNotificationHelpers = () => {
  const { addNotification, addSystemAlert } = useNotifications();

  const showSuccess = useCallback((title: string, message: string, duration?: number) => {
    addNotification({
      type: 'success',
      title,
      message,
      duration,
    });
  }, [addNotification]);

  const showError = useCallback((title: string, message: string, persistent = true) => {
    addNotification({
      type: 'error',
      title,
      message,
      duration: persistent ? 0 : 5000,
      persistent,
    });
  }, [addNotification]);

  const showWarning = useCallback((title: string, message: string, duration?: number) => {
    addNotification({
      type: 'warning',
      title,
      message,
      duration,
    });
  }, [addNotification]);

  const showInfo = useCallback((title: string, message: string, duration?: number) => {
    addNotification({
      type: 'info',
      title,
      message,
      duration,
    });
  }, [addNotification]);

  const showActionNotification = useCallback((
    title: string,
    message: string,
    actionLabel: string,
    actionCallback: () => void,
    type: 'success' | 'error' | 'warning' | 'info' = 'info'
  ) => {
    addNotification({
      type,
      title,
      message,
      action: {
        label: actionLabel,
        onClick: actionCallback,
      },
      persistent: true,
    });
  }, [addNotification]);

  const showMaintenanceAlert = useCallback((
    title: string,
    message: string,
    startTime?: Date,
    endTime?: Date,
    dismissible = true
  ) => {
    addSystemAlert({
      type: 'maintenance',
      title,
      message,
      startTime,
      endTime,
      dismissible,
    });
  }, [addSystemAlert]);

  const showSystemWarning = useCallback((
    title: string,
    message: string,
    dismissible = true
  ) => {
    addSystemAlert({
      type: 'warning',
      title,
      message,
      dismissible,
    });
  }, [addSystemAlert]);

  const showSystemInfo = useCallback((
    title: string,
    message: string,
    dismissible = true
  ) => {
    addSystemAlert({
      type: 'info',
      title,
      message,
      dismissible,
    });
  }, [addSystemAlert]);

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showActionNotification,
    showMaintenanceAlert,
    showSystemWarning,
    showSystemInfo,
  };
};

export default useNotificationHelpers;